import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db, storage } from '../firebase';
import {
  doc, getDoc, updateDoc, addDoc, collection, query, where, getDocs, orderBy, limit, serverTimestamp
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import AdminPanel from './AdminPanel';
import { useFriendActions } from "./useFriendActions";

// 스포츠 종목 상수
const SPORTS = ['탁구', '배드민턴', '테니스', '볼링'];

// MMR을 티어로 변환하는 함수
function getTierInfo(mmr) {
  if (mmr == null) mmr = 0;
  if (mmr < 1000) return { name: "루키", emoji: "🥚", color: "#8d6e63" };
  if (mmr < 1400) return { name: "엘리트", emoji: "🥈", color: "#9e9e9e" };
  if (mmr < 1800) return { name: "프로", emoji: "🥇", color: "#ffc107" };
  if (mmr < 2200) return { name: "챌린저", emoji: "🏆", color: "#ff9800" };
  return { name: "레전드", emoji: "👑", color: "#9c27b0" };
}

function Mypage() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [localUser, setLocalUser] = useState(null);

  // 사용자 정보 로드
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    setLocalUser(user);
  }, []);

  // 기본 상태 관리
  const [user, setUser] = useState(null);
  const [userDocId, setUserDocId] = useState("");
  const [profileImg, setProfileImg] = useState("");
  const [nickname, setNickname] = useState("");
  const [introduction, setIntroduction] = useState("");
  const [selectedSport, setSelectedSport] = useState('볼링');
  const [tier, setTier] = useState(getTierInfo(0));
  
  // UI 상태 관리
  const [editingNick, setEditingNick] = useState(false);
  const [editingIntro, setEditingIntro] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState("");
  
  // 친구 관련 상태
  const { friends, friendRequests, acceptFriend, removeFriend, requestFriend, msg: friendMsg, loading: friendLoading } = useFriendActions(user);
  
  // 실제 데이터 상태
  const [sportsRecords, setSportsRecords] = useState({});
  const [recentMatches, setRecentMatches] = useState([]);
  const [mmrHistory, setMmrHistory] = useState([]);
  const [guildInfo, setGuildInfo] = useState(null);
  const [achievements, setAchievements] = useState([]);

  // 관리자 기능 관련 상태
  const [isAdmin, setIsAdmin] = useState(false);
  const [pendingAssociations, setPendingAssociations] = useState([]);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [adminLoading, setAdminLoading] = useState(false);

  // 메모이제이션된 값들
  const isMine = useMemo(() => !userId || userId === localUser?.id, [userId, localUser?.id]);
  const targetUserId = useMemo(() => userId || localUser?.id, [userId, localUser?.id]);

  // 관리자 권한 확인
  const checkAdminStatus = useCallback(async () => {
    if (!localUser?.id) return;
    
    try {
      const q = query(collection(db, "users"), where("id", "==", localUser.id));
      const snap = await getDocs(q);
      
      if (!snap.empty) {
        const userData = snap.docs[0].data();
        setIsAdmin(userData.role === 'admin');
      }
    } catch (error) {
      console.error("관리자 권한 확인 실패:", error);
    }
  }, [localUser?.id]);

  // 승인 대기 중인 협회 목록 로드
  const fetchPendingAssociations = useCallback(async () => {
    if (!isAdmin) return;
    
    try {
      setAdminLoading(true);
      const q = query(
        collection(db, "users"),
        where("userType", "==", "association"),
        where("isApproved", "==", false)
      );
      const snap = await getDocs(q);
      
      const associations = snap.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setPendingAssociations(associations);
    } catch (error) {
      console.error("승인 대기 협회 로드 실패:", error);
      setMsg("승인 대기 협회 목록을 불러올 수 없습니다.");
    } finally {
      setAdminLoading(false);
    }
  }, [isAdmin]);

  // 협회 승인/거부 처리
  const handleAssociationApproval = useCallback(async (associationId, approved) => {
    if (!isAdmin) return;
    
    const action = approved ? "승인" : "거부";
    const confirmed = window.confirm(
      `정말로 이 협회를 ${action}하시겠습니까?\n\n` +
      `이 작업은 되돌릴 수 없습니다.`
    );
    
    if (!confirmed) return;
    
    try {
      setAdminLoading(true);
      
      const userRef = doc(db, "users", associationId);
      const userSnap = await getDoc(userRef);
      
      if (!userSnap.exists()) {
        setMsg("협회 정보를 찾을 수 없습니다.");
        return;
      }
      
      const associationData = userSnap.data();
      
      await updateDoc(userRef, {
        isApproved: approved,
        approvedAt: serverTimestamp(),
        approvedBy: localUser.id
      });
      
      if (approved) {
        await addDoc(collection(db, 'notifications'), {
          type: 'association_approved',
          to: associationData.id,
          message: `🎉 축하합니다! ${associationData.associationName} 협회가 승인되었습니다. 이제 로그인하여 서비스를 이용하실 수 있습니다.`,
          status: 'unread',
          createdAt: serverTimestamp()
        });
      } else {
        await addDoc(collection(db, 'notifications'), {
          type: 'association_rejected',
          to: associationData.id,
          message: `❌ ${associationData.associationName} 협회 신청이 거부되었습니다. 자세한 사항은 관리자에게 문의해주세요.`,
          status: 'unread',
          createdAt: serverTimestamp()
        });
      }
      
      const successMsg = approved 
        ? `✅ ${associationData.associationName} 협회가 성공적으로 승인되었습니다!`
        : `❌ ${associationData.associationName} 협회가 거부되었습니다.`;
      
      setMsg(successMsg);
      
      setTimeout(() => {
        setMsg("");
      }, 3000);
      
      await fetchPendingAssociations();
      
    } catch (error) {
      console.error("협회 승인 처리 실패:", error);
      setMsg("승인 처리 중 오류가 발생했습니다.");
    } finally {
      setAdminLoading(false);
    }
  }, [isAdmin, localUser?.id, fetchPendingAssociations]);

  // 사용자 등급 변경 (관리자 전용)
  const handleRoleChange = useCallback(async (userId, newRole) => {
    if (!isAdmin) return;
    
    const userRef = doc(db, "users", userId);
    const userSnap = await getDoc(userRef);
    
    if (!userSnap.exists()) {
      setMsg("사용자 정보를 찾을 수 없습니다.");
      return;
    }
    
    const currentUserData = userSnap.data();
    const currentRole = currentUserData.role || 'user';
    
    if (currentRole === newRole) {
      setMsg("이미 같은 등급입니다.");
      return;
    }
    
    const roleText = newRole === 'admin' ? '관리자' : '일반 사용자';
    const confirmed = window.confirm(
      `정말로 이 사용자의 등급을 ${roleText}로 변경하시겠습니까?\n\n` +
      `사용자: ${currentUserData.nickname || currentUserData.id}\n` +
      `현재 등급: ${currentRole === 'admin' ? '관리자' : '일반 사용자'}\n` +
      `변경할 등급: ${roleText}`
    );
    
    if (!confirmed) return;
    
    try {
      setAdminLoading(true);
      
      await updateDoc(userRef, {
        role: newRole,
        roleChangedAt: serverTimestamp(),
        roleChangedBy: localUser.id
      });
      
      await addDoc(collection(db, 'notifications'), {
        type: 'role_changed',
        to: userId,
        message: `🔔 계정 등급이 ${roleText}로 변경되었습니다.`,
        status: 'unread',
        createdAt: serverTimestamp()
      });
      
      const successMsg = `✅ ${currentUserData.nickname || currentUserData.id} 사용자의 등급이 ${roleText}로 변경되었습니다!`;
      setMsg(successMsg);
      
      setTimeout(() => {
        setMsg("");
      }, 3000);
      
      if (userId === targetUserId) {
        const q = query(collection(db, "users"), where("id", "==", targetUserId));
        const snap = await getDocs(q);
        
        if (!snap.empty) {
          const userData = snap.docs[0].data();
          const docId = snap.docs[0].id;
          
          setUser(userData);
          setUserDocId(docId);
          setProfileImg(userData.profileImg || "");
          setNickname(userData.nickname || userData.id);
          setIntroduction(userData.introduction || "");
          
          const currentMmr = userData.mmr || 0;
          setTier(getTierInfo(currentMmr));
        }
      }
    } catch (error) {
      console.error("등급 변경 실패:", error);
      setMsg("등급 변경 중 오류가 발생했습니다.");
    } finally {
      setAdminLoading(false);
    }
  }, [isAdmin, localUser?.id, targetUserId]);

  // 사용자 기본 정보 로드
  const fetchUserProfile = useCallback(async () => {
    if (!targetUserId) return;
    
    try {
      const q = query(collection(db, "users"), where("id", "==", targetUserId));
      const snap = await getDocs(q);
      
      if (!snap.empty) {
        const userData = snap.docs[0].data();
        const docId = snap.docs[0].id;
        
        setUser(userData);
        setUserDocId(docId);
        setProfileImg(userData.profileImg || "");
        setNickname(userData.nickname || userData.id);
        setIntroduction(userData.introduction || "");
        
        const currentMmr = userData.mmr || 0;
        setTier(getTierInfo(currentMmr));
      }
    } catch (error) {
      console.error("사용자 정보 로드 실패:", error);
      setMsg("사용자 정보를 불러올 수 없습니다.");
    }
  }, [targetUserId]);

  // 스포츠별 전적 로드
  const fetchSportsRecords = useCallback(async () => {
    if (!targetUserId) return;
    
    try {
      const recordsData = {};
      
      for (const sport of SPORTS) {
        if (sport === '볼링') {
          const matchQuery = query(
            collection(db, 'bowlingMatches'),
            where('participants', 'array-contains', targetUserId),
            where('status', '==', 'completed')
          );
          const matchSnap = await getDocs(matchQuery);
          
          let wins = 0;
          let losses = 0;
          let totalMyScore = 0;
          let totalOppScore = 0;
          let gameCount = 0;
          
          matchSnap.docs.forEach(doc => {
            const matchData = doc.data();
            const scores = matchData.scores || {};
            const myScores = scores[targetUserId];
            const opponentId = matchData.participants.find(id => id !== targetUserId);
            const oppScores = scores[opponentId];
            
            if (myScores && oppScores) {
              const myTotal = myScores.reduce((sum, game) => sum + (parseInt(game.my) || 0), 0);
              const oppTotal = oppScores.reduce((sum, game) => sum + (parseInt(game.opponent) || 0), 0);
              
              if (myTotal > oppTotal) wins++;
              else if (myTotal < oppTotal) losses++;
              
              totalMyScore += myTotal;
              totalOppScore += oppTotal;
              gameCount++;
            }
          });
          
          recordsData[sport] = {
            win: wins,
            lose: losses,
            mmr: user?.mmr || 0,
            tier: getTierInfo(user?.mmr || 0),
            winRate: wins + losses > 0 ? Math.round((wins / (wins + losses)) * 100) : 0,
            avgScore: gameCount > 0 ? Math.round(totalMyScore / gameCount) : 0,
            totalGames: gameCount
          };
        } else {
          recordsData[sport] = {
            win: 0,
            lose: 0,
            mmr: 0,
            tier: getTierInfo(0),
            winRate: 0,
            avgScore: 0,
            totalGames: 0
          };
        }
      }
      
      setSportsRecords(recordsData);
    } catch (error) {
      console.error("전적 로드 실패:", error);
    }
  }, [targetUserId, user?.mmr]);

  // 최근 경기 기록 로드 (볼링만)
  const fetchRecentMatches = useCallback(async () => {
    if (!targetUserId) return;
    
    try {
      const matchQuery = query(
        collection(db, 'bowlingMatches'),
        where('participants', 'array-contains', targetUserId),
        where('status', '==', 'completed'),
        orderBy('completedAt', 'desc'),
        limit(10)
      );
      const matchSnap = await getDocs(matchQuery);
      
      const matchesData = [];
      for (const matchDoc of matchSnap.docs) {
        const matchData = matchDoc.data();
        const opponentId = matchData.participants.find(id => id !== targetUserId);
        
        const userQuery = query(
          collection(db, 'users'),
          where('id', '==', opponentId)
        );
        const userSnap = await getDocs(userQuery);
        
        if (!userSnap.empty) {
          const opponentData = userSnap.docs[0].data();
          const scores = matchData.scores || {};
          const myScores = scores[targetUserId];
          const oppScores = scores[opponentId];
          
          if (myScores && oppScores) {
            const myTotal = myScores.reduce((sum, game) => sum + (parseInt(game.my) || 0), 0);
            const oppTotal = oppScores.reduce((sum, game) => sum + (parseInt(game.opponent) || 0), 0);
            
            matchesData.push({
              id: matchDoc.id,
              opponent: opponentData.nickname || opponentData.id,
              opponentMMR: opponentData.mmr || 0,
              myScore: myTotal,
              oppScore: oppTotal,
              result: myTotal > oppTotal ? 'win' : myTotal < oppTotal ? 'lose' : 'draw',
              type: matchData.type || 'ranking',
              date: matchData.completedAt?.toDate() || new Date(),
              sport: '볼링'
            });
          }
        }
      }
      
      setRecentMatches(matchesData);
    } catch (error) {
      console.error("최근 경기 로드 실패:", error);
    }
  }, [targetUserId]);

  // MMR 히스토리 로드 (볼링만)
  const fetchMmrHistory = useCallback(async () => {
    if (!targetUserId || selectedSport !== '볼링') return;
    
    try {
      const historyQuery = query(
        collection(db, 'mmrHistory'),
        where('userId', '==', targetUserId),
        where('sport', '==', '볼링'),
        orderBy('date', 'asc'),
        limit(20)
      );
      const historySnap = await getDocs(historyQuery);
      
      const historyData = historySnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        date: doc.data().date?.toDate() || new Date()
      }));
      
      setMmrHistory(historyData);
    } catch (error) {
      console.error("MMR 히스토리 로드 실패:", error);
    }
  }, [targetUserId, selectedSport]);

  // 길드 정보 로드
  const fetchGuildInfo = useCallback(async () => {
    if (!targetUserId) return;
    
    try {
      const memberQuery = query(
        collection(db, 'guildMembers'),
        where('userId', '==', targetUserId)
      );
      const memberSnap = await getDocs(memberQuery);
      
      if (!memberSnap.empty) {
        const memberData = memberSnap.docs[0].data();
        
        const guildDoc = await getDoc(doc(db, 'guilds', memberData.guildId));
        if (guildDoc.exists()) {
          const guildData = guildDoc.data();
          setGuildInfo({
            id: guildDoc.id,
            name: guildData.name,
            role: memberData.role,
            joinDate: memberData.joinDate?.toDate() || new Date(),
            sport: guildData.sport
          });
        }
      }
    } catch (error) {
      console.error("길드 정보 로드 실패:", error);
    }
  }, [targetUserId]);

  // 업적 생성
  const generateAchievements = useCallback(() => {
    const newAchievements = [];
    const bowlingRecord = sportsRecords['볼링'];
    
    if (bowlingRecord) {
      if (bowlingRecord.win >= 1) {
        newAchievements.push({
          icon: '🎯',
          title: '첫 승리',
          description: '첫 번째 승리를 달성했습니다',
          rarity: 'common'
        });
      }
      
      if (bowlingRecord.win >= 10) {
        newAchievements.push({
          icon: '🏅',
          title: '승리의 시작',
          description: '10승을 달성했습니다',
          rarity: 'rare'
        });
      }
      
      if (bowlingRecord.win >= 50) {
        newAchievements.push({
          icon: '🏆',
          title: '볼링 마스터',
          description: '50승을 달성했습니다',
          rarity: 'epic'
        });
      }
      
      if (bowlingRecord.winRate >= 80 && bowlingRecord.totalGames >= 10) {
        newAchievements.push({
          icon: '⭐',
          title: '승률왕',
          description: '80% 이상의 승률을 유지하고 있습니다',
          rarity: 'epic'
        });
      }
      
      if (bowlingRecord.mmr >= 2000) {
        newAchievements.push({
          icon: '👑',
          title: '레전드 도달',
          description: 'MMR 2000점을 달성했습니다',
          rarity: 'legendary'
        });
      }
    }
    
    if (guildInfo) {
      newAchievements.push({
        icon: '🏰',
        title: '길드 멤버',
        description: `${guildInfo.name} 길드에 소속되어 있습니다`,
        rarity: 'common'
      });
      
      if (guildInfo.role === 'leader') {
        newAchievements.push({
          icon: '👑',
          title: '길드장',
          description: '길드를 이끌고 있습니다',
          rarity: 'rare'
        });
      }
    }
    
    setAchievements(newAchievements);
  }, [sportsRecords, guildInfo]);

  // 친구 상태 확인 (useFriendActions 훅에서 처리됨)
  const checkFriendStatus = useCallback(async () => {
    // useFriendActions 훅에서 친구 상태를 자동으로 관리함
  }, []);

  // 프로필 이미지 업로드
  const handleImageUpload = useCallback(async (e) => {
    const file = e.target.files[0];
    if (!file || !userDocId) return;
    
    setUploading(true);
    setMsg("");
    
    try {
      if (profileImg && profileImg.includes("firebase")) {
        try {
          const path = profileImg.split("/o/")[1].split("?")[0];
          await deleteObject(ref(storage, decodeURIComponent(path)));
        } catch (err) {
          console.log("기존 이미지 삭제 실패:", err);
        }
      }
      
      const storageRef = ref(storage, `profile/${localUser.id}_${Date.now()}_${file.name}`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      
      await updateDoc(doc(db, "users", userDocId), { profileImg: url });
      
      setProfileImg(url);
      await fetchUserProfile();
      setMsg("프로필 이미지가 업데이트되었습니다.");
      
      const updatedUser = { ...localUser, profileImg: url };
      localStorage.setItem("user", JSON.stringify(updatedUser));
      
    } catch (error) {
      console.error("이미지 업로드 실패:", error);
      setMsg("이미지 업로드에 실패했습니다.");
    } finally {
      setUploading(false);
    }
  }, [profileImg, userDocId, localUser, fetchUserProfile]);

  // 닉네임 저장
  const saveNickname = useCallback(async () => {
    if (!nickname.trim() || !userDocId) {
      setMsg("닉네임을 입력해주세요.");
      return;
    }
    
    try {
      await updateDoc(doc(db, "users", userDocId), { nickname });
      setEditingNick(false);
      setMsg("닉네임이 변경되었습니다.");
      
      const updatedUser = { ...localUser, nickname };
      localStorage.setItem("user", JSON.stringify(updatedUser));
      
    } catch (error) {
      console.error("닉네임 저장 실패:", error);
      setMsg("닉네임 변경에 실패했습니다.");
    }
  }, [nickname, userDocId, localUser]);

  // 소개 저장
  const saveIntroduction = useCallback(async () => {
    if (!userDocId) return;
    
    try {
      await updateDoc(doc(db, "users", userDocId), { introduction });
      setEditingIntro(false);
      setMsg("소개가 변경되었습니다.");
    } catch (error) {
      console.error("소개 저장 실패:", error);
      setMsg("소개 변경에 실패했습니다.");
    }
  }, [introduction, userDocId]);

  // 친구 추가
  const sendFriendRequest = useCallback(async () => {
    if (!localUser?.id || !targetUserId) return;
    
    try {
      await addDoc(collection(db, 'friendRequests'), {
        from: localUser.id,
        to: targetUserId,
        status: 'pending',
        createdAt: serverTimestamp()
      });
      
      await addDoc(collection(db, 'notifications'), {
        type: 'friend_request',
        from: localUser.id,
        to: targetUserId,
        message: `${localUser.nickname || localUser.id}님이 친구 요청을 보냈습니다.`,
        status: 'unread',
        createdAt: serverTimestamp()
      });
      
      setMsg("친구 요청을 보냈습니다.");
    } catch (error) {
      console.error("친구 요청 실패:", error);
      setMsg("친구 요청에 실패했습니다.");
    }
  }, [localUser, targetUserId]);

  // 친구목록 불러오기 (useFriendActions 훅에서 처리됨)
  useEffect(() => {
    // useFriendActions 훅에서 친구 목록을 자동으로 관리함
  }, [user]);

  // 초기 데이터 로드
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        await fetchUserProfile();
        await checkFriendStatus();
        await checkAdminStatus();
      } catch (error) {
        console.error("데이터 로드 실패:", error);
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [targetUserId, fetchUserProfile, checkFriendStatus, checkAdminStatus]);

  useEffect(() => {
    if (isAdmin) {
      fetchPendingAssociations();
    }
  }, [isAdmin, fetchPendingAssociations]);

  useEffect(() => {
    if (user) {
      fetchSportsRecords();
      fetchRecentMatches();
      fetchGuildInfo();
    }
  }, [user, fetchSportsRecords, fetchRecentMatches, fetchGuildInfo]);

  useEffect(() => {
    if (user && selectedSport) {
      fetchMmrHistory();
    }
  }, [user, selectedSport, fetchMmrHistory]);

  useEffect(() => {
    if (Object.keys(sportsRecords).length > 0) {
      generateAchievements();
    }
  }, [sportsRecords, guildInfo, generateAchievements]);

  useEffect(() => {
    if (!msg) return;
    const timer = setTimeout(() => setMsg(""), 3000);
    return () => clearTimeout(timer);
  }, [msg]);

  // PWA 모바일 최적화 스타일
  const styles = {
    container: {
      minHeight: '100vh',
      background: '#f8fafc',
      paddingBottom: 'env(safe-area-inset-bottom, 20px)'
    },
    statusBar: {
      height: 'env(safe-area-inset-top, 0px)',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
    },
    header: {
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: '#fff',
      padding: '16px',
      display: 'flex',
      alignItems: 'center',
      position: 'sticky',
      top: 0,
      zIndex: 100,
      backdropFilter: 'blur(20px)',
      WebkitBackdropFilter: 'blur(20px)',
      borderBottom: '1px solid rgba(255,255,255,0.1)'
    },
    backButton: {
      background: 'rgba(255,255,255,0.15)',
      border: 'none',
      borderRadius: '50%',
      width: '40px',
      height: '40px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '18px',
      color: '#fff',
      cursor: 'pointer',
      marginRight: '12px',
      transition: 'all 0.2s',
      WebkitTapHighlightColor: 'transparent'
    },
    headerTitle: {
      fontSize: '18px',
      fontWeight: '600',
      flex: 1,
      textAlign: 'center',
      marginRight: '52px'
    },
    profileSection: {
      background: '#fff',
      margin: '0 16px 16px 16px',
      borderRadius: '20px',
      padding: '24px 20px',
      boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
      border: '1px solid rgba(255,255,255,0.8)',
      position: 'relative',
      marginTop: '-20px'
    },
    profileImageContainer: {
      position: 'relative',
      width: '100px',
      height: '100px',
      margin: '0 auto 20px auto',
      borderRadius: '25px',
      overflow: 'hidden',
      background: 'linear-gradient(135deg, #f1f5f9, #e2e8f0)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '40px',
      color: '#64748b',
      border: '4px solid #fff',
      boxShadow: '0 8px 25px rgba(0,0,0,0.15)'
    },
    profileImage: {
      width: '100%',
      height: '100%',
      objectFit: 'cover'
    },
    imageUploadButton: {
      position: 'absolute',
      bottom: '-10px',
      right: '-10px',
      width: '36px',
      height: '36px',
      background: 'linear-gradient(135deg, #667eea, #764ba2)',
      borderRadius: '50%',
      border: '3px solid #fff',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      fontSize: '16px',
      color: '#fff',
      boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
      transition: 'all 0.2s',
      WebkitTapHighlightColor: 'transparent'
    },
    hiddenInput: {
      display: 'none'
    },
    userInfo: {
      textAlign: 'center',
      marginBottom: '20px'
    },
    nicknameContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      marginBottom: '12px'
    },
    nickname: {
      fontSize: '24px',
      fontWeight: '700',
      color: '#1e293b',
      margin: 0
    },
    editButton: {
      background: 'rgba(102, 126, 234, 0.1)',
      border: 'none',
      borderRadius: '8px',
      width: '32px',
      height: '32px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '14px',
      cursor: 'pointer',
      transition: 'all 0.2s',
      WebkitTapHighlightColor: 'transparent'
    },
    editInputContainer: {
      background: '#f8fafc',
      borderRadius: '16px',
      padding: '16px',
      margin: '8px 0'
    },
    editInput: {
      width: '100%',
      fontSize: '16px',
      fontWeight: '600',
      padding: '12px 16px',
      border: '2px solid #e2e8f0',
      borderRadius: '12px',
      textAlign: 'center',
      background: '#fff',
      outline: 'none',
      transition: 'border-color 0.2s',
      WebkitAppearance: 'none'
    },
    editButtons: {
      display: 'flex',
      gap: '8px',
      justifyContent: 'center',
      marginTop: '12px'
    },
    saveButton: {
      background: 'linear-gradient(135deg, #10b981, #059669)',
      color: '#fff',
      border: 'none',
      padding: '10px 20px',
      borderRadius: '12px',
      fontSize: '14px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.2s',
      WebkitTapHighlightColor: 'transparent'
    },
    cancelButton: {
      background: '#6b7280',
      color: '#fff',
      border: 'none',
      padding: '10px 20px',
      borderRadius: '12px',
      fontSize: '14px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.2s',
      WebkitTapHighlightColor: 'transparent'
    },
    introduction: {
      color: '#64748b',
      fontSize: '14px',
      lineHeight: '1.6',
      marginBottom: '16px',
      padding: '0 8px'
    },
    tierBadge: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '8px',
      background: 'linear-gradient(135deg, #fbbf24, #f59e0b)',
      color: '#fff',
      padding: '10px 20px',
      borderRadius: '25px',
      fontSize: '14px',
      fontWeight: '700',
      boxShadow: '0 6px 20px rgba(251, 191, 36, 0.3)',
      border: '2px solid rgba(255,255,255,0.2)'
    },
    tabContainer: {
      background: '#fff',
      margin: '0 16px 16px 16px',
      borderRadius: '16px',
      padding: '6px',
      boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
      border: '1px solid rgba(255,255,255,0.8)'
    },
    tabScroll: {
      display: 'flex',
      gap: '4px',
      overflowX: 'auto',
      paddingBottom: '2px',
      scrollbarWidth: 'none',
      msOverflowStyle: 'none'
    },
    tabButton: {
      flex: '0 0 auto',
      padding: '12px 18px',
      borderRadius: '12px',
      border: 'none',
      background: 'transparent',
      color: '#64748b',
      fontSize: '14px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.2s',
      whiteSpace: 'nowrap',
      WebkitTapHighlightColor: 'transparent',
      minWidth: '70px'
    },
    activeTab: {
      background: 'linear-gradient(135deg, #667eea, #764ba2)',
      color: '#fff',
      boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)',
      transform: 'scale(1.02)'
    },
    adminTab: {
      background: 'linear-gradient(135deg, #ef4444, #dc2626)',
      color: '#fff',
      boxShadow: '0 4px 15px rgba(239, 68, 68, 0.3)',
      transform: 'scale(1.02)'
    },
    contentCard: {
      background: '#fff',
      margin: '0 16px 16px 16px',
      borderRadius: '18px',
      padding: '20px',
      boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
      border: '1px solid rgba(255,255,255,0.8)'
    },
    cardTitle: {
      fontSize: '18px',
      fontWeight: '700',
      color: '#1e293b',
      marginBottom: '18px',
      display: 'flex',
      alignItems: 'center',
      gap: '10px'
    },
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: '12px',
      marginBottom: '16px'
    },
    statItem: {
      background: 'linear-gradient(135deg, #f8fafc, #f1f5f9)',
      padding: '18px 16px',
      borderRadius: '16px',
      textAlign: 'center',
      border: '1px solid #e2e8f0',
      transition: 'all 0.2s'
    },
    statLabel: {
      fontSize: '12px',
      color: '#64748b',
      marginBottom: '6px',
      fontWeight: '500',
      textTransform: 'uppercase',
      letterSpacing: '0.5px'
    },
    statValue: {
      fontSize: '22px',
      fontWeight: '800',
      color: '#1e293b'
    },
    matchList: {
      maxHeight: '300px',
      overflowY: 'auto'
    },
    matchItem: {
      background: 'linear-gradient(135deg, #f8fafc, #f1f5f9)',
      padding: '16px',
      borderRadius: '14px',
      marginBottom: '12px',
      border: '1px solid #e2e8f0',
      transition: 'all 0.2s'
    },
    matchHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '8px'
    },
    matchDate: {
      fontSize: '12px',
      color: '#64748b',
      fontWeight: '500'
    },
    matchResultBadge: {
      fontSize: '11px',
      fontWeight: '700',
      padding: '4px 10px',
      borderRadius: '12px',
      textTransform: 'uppercase',
      letterSpacing: '0.5px'
    },
    winBadge: {
      background: 'linear-gradient(135deg, #dcfce7, #bbf7d0)',
      color: '#166534',
      border: '1px solid #86efac'
    },
    loseBadge: {
      background: 'linear-gradient(135deg, #fee2e2, #fecaca)',
      color: '#991b1b',
      border: '1px solid #fca5a5'
    },
    matchScore: {
      fontSize: '18px',
      fontWeight: '700',
      color: '#1e293b',
      textAlign: 'center',
      marginBottom: '4px'
    },
    matchOpponent: {
      fontSize: '13px',
      color: '#64748b',
      textAlign: 'center',
      fontWeight: '500'
    },
    achievementsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))',
      gap: '12px'
    },
    achievementItem: {
      background: 'linear-gradient(135deg, #f8fafc, #f1f5f9)',
      padding: '16px 12px',
      borderRadius: '16px',
      textAlign: 'center',
      border: '1px solid #e2e8f0',
      transition: 'all 0.2s'
    },
    achievementIcon: {
      fontSize: '28px',
      marginBottom: '8px',
      display: 'block'
    },
    achievementTitle: {
      fontSize: '12px',
      fontWeight: '700',
      color: '#1e293b',
      marginBottom: '4px'
    },
    achievementDesc: {
      fontSize: '10px',
      color: '#64748b',
      lineHeight: '1.3'
    },
    mmrChart: {
      height: '220px',
      display: 'flex',
      alignItems: 'end',
      justifyContent: 'space-between',
      padding: '30px 0 40px 0',
      borderBottom: '2px solid #e2e8f0',
      gap: '3px',
      overflowX: 'auto'
    },
    mmrBar: {
      background: 'linear-gradient(to top, #667eea, #764ba2)',
      borderRadius: '6px 6px 0 0',
      flex: '1',
      position: 'relative',
      minHeight: '20px',
      minWidth: '20px',
      transition: 'all 0.2s'
    },
    mmrValue: {
      position: 'absolute',
      top: '-30px',
      left: '50%',
      transform: 'translateX(-50%)',
      fontSize: '11px',
      fontWeight: '700',
      color: '#374151',
      whiteSpace: 'nowrap',
      background: '#fff',
      padding: '2px 6px',
      borderRadius: '6px',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
    },
    mmrDate: {
      position: 'absolute',
      bottom: '-35px',
      left: '50%',
      transform: 'translateX(-50%) rotate(-45deg)',
      fontSize: '9px',
      color: '#6b7280',
      whiteSpace: 'nowrap',
      transformOrigin: 'center'
    },
    messageToast: {
      position: 'fixed',
      top: 'env(safe-area-inset-top, 20px)',
      left: '16px',
      right: '16px',
      background: 'linear-gradient(135deg, #dcfce7, #bbf7d0)',
      color: '#166534',
      padding: '14px 16px',
      borderRadius: '14px',
      fontSize: '14px',
      fontWeight: '600',
      border: '1px solid #86efac',
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      zIndex: 1000,
      boxShadow: '0 10px 30px rgba(0,0,0,0.15)',
      backdropFilter: 'blur(10px)',
      WebkitBackdropFilter: 'blur(10px)'
    },
    loadingContainer: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '70vh',
      background: '#f8fafc',
      padding: '20px'
    },
    loadingSpinner: {
      width: '50px',
      height: '50px',
      border: '4px solid #f1f5f9',
      borderTop: '4px solid #667eea',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite',
      marginBottom: '20px'
    },
    loadingText: {
      fontSize: '16px',
      color: '#64748b',
      fontWeight: '500',
      textAlign: 'center'
    },
    errorContainer: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '70vh',
      background: '#f8fafc',
      padding: '20px',
      textAlign: 'center'
    },
    errorIcon: {
      fontSize: '64px',
      marginBottom: '20px'
    },
    errorTitle: {
      fontSize: '22px',
      fontWeight: '700',
      color: '#1e293b',
      marginBottom: '12px'
    },
    errorButton: {
      background: 'linear-gradient(135deg, #667eea, #764ba2)',
      color: '#fff',
      border: 'none',
      padding: '14px 28px',
      borderRadius: '14px',
      fontSize: '16px',
      fontWeight: '600',
      cursor: 'pointer',
      marginTop: '20px',
      transition: 'all 0.2s',
      WebkitTapHighlightColor: 'transparent'
    },
    noDataContainer: {
      textAlign: 'center',
      padding: '40px 20px',
      color: '#64748b'
    },
    noDataIcon: {
      fontSize: '48px',
      marginBottom: '16px',
      opacity: 0.6
    },
    noDataText: {
      fontSize: '14px',
      fontWeight: '500',
      lineHeight: '1.5'
    }
  };

  // 닉네임 편집 UI
  const renderNickname = () => (
    editingNick && isMine ? (
      <div style={styles.editInputContainer}>
        <input
          value={nickname}
          onChange={e => setNickname(e.target.value)}
          style={styles.editInput}
          placeholder="닉네임을 입력하세요"
          autoFocus
        />
        <div style={styles.editButtons}>
          <button onClick={saveNickname} style={styles.saveButton}>저장</button>
          <button onClick={() => setEditingNick(false)} style={styles.cancelButton}>취소</button>
        </div>
      </div>
    ) : (
      <div style={styles.nicknameContainer}>
        <h2 style={styles.nickname}>{nickname}</h2>
        {isMine && (
          <button 
            onClick={() => setEditingNick(true)} 
            style={styles.editButton}
          >
            ✏️
          </button>
        )}
      </div>
    )
  );

  // 소개글 편집 UI
  const renderIntroduction = () => (
    editingIntro && isMine ? (
      <div style={styles.editInputContainer}>
        <input
          value={introduction}
          onChange={e => setIntroduction(e.target.value)}
          style={styles.editInput}
          placeholder="소개글을 입력하세요"
          autoFocus
        />
        <div style={styles.editButtons}>
          <button onClick={saveIntroduction} style={styles.saveButton}>저장</button>
          <button onClick={() => setEditingIntro(false)} style={styles.cancelButton}>취소</button>
        </div>
      </div>
    ) : (
      <div style={styles.introduction}>
        <span>{introduction || "한줄소개를 입력해보세요"}</span>
        {isMine && <button onClick={() => setEditingIntro(true)} style={{ marginLeft: 8, background: 'none', border: 'none', color: '#6366f1', cursor: 'pointer', fontSize: 16 }}>✏️</button>}
      </div>
    )
  );

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.statusBar}></div>
        <div style={styles.loadingContainer}>
          <div style={styles.loadingSpinner}></div>
          <div style={styles.loadingText}>프로필을 불러오는 중...</div>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div style={styles.container}>
        <div style={styles.statusBar}></div>
        <div style={styles.errorContainer}>
          <div style={styles.errorIcon}>😕</div>
          <div style={styles.errorTitle}>사용자를 찾을 수 없습니다</div>
          <button 
            onClick={() => navigate(-1)}
            style={styles.errorButton}
          >
            돌아가기
          </button>
        </div>
      </div>
    );
  }

  const currentRecord = sportsRecords[selectedSport] || { 
    win: 0, 
    lose: 0, 
    winRate: 0, 
    mmr: 0, 
    tier: getTierInfo(0),
    avgScore: 0,
    totalGames: 0
  };

  return (
    <>
      {/* 사용자 타입 표시 */}
      {isMine && (
        <div style={{
          background: user?.role === 'admin' 
            ? 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)' 
            : 'linear-gradient(135deg, #10b981 0%, #34d399 100%)',
          color: '#fff',
          padding: '8px 16px',
          borderRadius: 20,
          fontSize: 14,
          fontWeight: 600,
          textAlign: 'center',
          marginBottom: 16,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          {user?.role === 'admin' ? '👑 관리자' : '👤 일반 사용자'}
        </div>
      )}

      {/* 길드 정보 섹션 - 항상 프로필 아래에 위치 */}
      <div style={{ marginTop: 0, marginBottom: 32 }}>
        <h3 style={{ fontSize: 20, fontWeight: 800, color: '#6366f1', marginBottom: 16, letterSpacing: 1 }}>🏰 내 길드</h3>
        <div style={{
          background: 'linear-gradient(135deg, #e0e7ff 0%, #f3f4f6 100%)',
          borderRadius: 18,
          boxShadow: '0 4px 18px #6366f122',
          padding: '28px 24px',
          display: 'flex',
          alignItems: 'center',
          gap: 24,
          minHeight: 80,
          justifyContent: 'space-between',
          marginBottom: 8
        }}>
          {guildInfo ? (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 18 }}>
                <span style={{ fontSize: 36 }}>🏰</span>
                <div>
                  <div style={{ fontWeight: 800, fontSize: 18, color: '#3730a3' }}>{guildInfo.name}</div>
                  <div style={{ fontSize: 13, color: '#6366f1', fontWeight: 600 }}>{guildInfo.role === 'leader' ? '길드장' : '길드원'}</div>
                </div>
              </div>
              {/* 내 마이페이지일 때만 버튼 활성화, 상대방은 비활성화 */}
              <button
                onClick={() => {
                  if (isMine && guildInfo && guildInfo.id) {
                    navigate(`/guild/${guildInfo.id}`);
                  }
                }}
                disabled={!isMine}
                style={{
                  background: isMine ? 'linear-gradient(135deg, #6366f1, #a5b4fc)' : '#e5e7eb',
                  color: isMine ? '#fff' : '#a5b4fc',
                  border: 'none',
                  borderRadius: 10,
                  padding: '10px 22px',
                  fontWeight: 700,
                  fontSize: 15,
                  boxShadow: '0 2px 8px #6366f122',
                  cursor: isMine ? 'pointer' : 'not-allowed',
                  opacity: isMine ? 1 : 0.7
                }}
              >
                길드로 이동
              </button>
            </>
          ) : (
            <div style={{ color: '#64748b', fontSize: 16, fontWeight: 600 }}>아직 가입한 길드가 없습니다.</div>
          )}
        </div>
      </div>

      {/* 프로필 헤더 */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 24, 
        marginBottom: 32,
        background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
        padding: 24,
        borderRadius: 20,
        boxShadow: '0 4px 16px rgba(0,0,0,0.05)'
      }}>
        <div style={{ position: 'relative' }}>
          <div style={{
            width: 100, height: 100, borderRadius: 25, overflow: 'hidden',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 4px 16px rgba(102,126,234,0.3)',
            border: '4px solid #fff'
          }}>
            {profileImg ? 
              <img src={profileImg + '?t=' + Date.now()} alt="프로필" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> 
              : <span style={{ fontSize: 40, color: '#fff' }}>👤</span>
            }
          </div>
          {/* 사진 업로드 버튼 */}
          {isMine && (
            <label style={{
              position: 'absolute', bottom: 0, right: 0, background: '#6366f1',
              borderRadius: '50%', width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#fff', cursor: 'pointer', boxShadow: '0 4px 12px rgba(99,102,241,0.4)', border: '3px solid #fff',
              transition: 'all 0.2s ease'
            }}>
              <input type="file" accept="image/*" onChange={handleImageUpload} style={{ display: 'none' }} />
              {uploading ? '⏳' : '📷'}
            </label>
          )}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 8 }}>
            <h2 style={{ fontSize: 28, fontWeight: 800, color: '#1e293b', margin: 0 }}>{nickname}</h2>
            {currentRecord.tier && (
              <span style={{
                background: currentRecord.tier.color,
                color: '#fff',
                padding: '4px 12px',
                borderRadius: 12,
                fontSize: 12,
                fontWeight: 600
              }}>
                {currentRecord.tier.emoji} {currentRecord.tier.name}
              </span>
            )}
          </div>
          {/* 한줄소개 */}
          {editingIntro ? (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input 
                value={introduction} 
                onChange={e => setIntroduction(e.target.value)}
                style={{ 
                  fontSize: 15, 
                  padding: '8px 12px', 
                  borderRadius: 10, 
                  border: '2px solid #c7d2fe', 
                  outline: 'none', 
                  flex: 1,
                  background: '#fff'
                }} 
              />
              <button 
                onClick={saveIntroduction} 
                style={{ 
                  background: '#6366f1', 
                  color: '#fff', 
                  border: 'none', 
                  borderRadius: 10, 
                  padding: '8px 16px', 
                  fontWeight: 600, 
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                저장
              </button>
              <button 
                onClick={() => setEditingIntro(false)} 
                style={{ 
                  background: '#e5e7eb', 
                  color: '#6366f1', 
                  border: 'none', 
                  borderRadius: 10, 
                  padding: '8px 16px', 
                  fontWeight: 600, 
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                취소
              </button>
            </div>
          ) : (
            <div style={{ 
              color: '#64748b', 
              fontSize: 16, 
              lineHeight: 1.5,
              background: '#fff',
              padding: '12px 16px',
              borderRadius: 10,
              border: '1px solid #e2e8f0'
            }}>
              {introduction || "한줄소개를 입력해보세요"}
              {isMine && (
                <button 
                  onClick={() => setEditingIntro(true)} 
                  style={{ 
                    marginLeft: 8, 
                    background: 'none', 
                    border: 'none', 
                    color: '#6366f1', 
                    cursor: 'pointer', 
                    fontSize: 16,
                    transition: 'all 0.2s ease'
                  }}
                >
                  ✏️
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* MBTI(APTI) 정보 메인 카드 */}
      {user?.APTI && (
        <div style={{
          background: 'linear-gradient(135deg, #6366f1 0%, #a5b4fc 100%)',
          borderRadius: 20,
          padding: '24px 20px',
          marginBottom: 32,
          display: 'flex',
          alignItems: 'center',
          gap: 24,
          boxShadow: '0 4px 16px #6366f122',
          color: '#fff',
          justifyContent: 'center'
        }}>
          <span style={{ fontSize: 56 }}>{user.APTI.icon || '🧠'}</span>
          <div>
            <div style={{ fontSize: 28, fontWeight: 900, letterSpacing: 2 }}>{user.APTI.type}</div>
            <div style={{ fontSize: 18, fontWeight: 600 }}>{user.APTI.title}</div>
            <div style={{ fontSize: 14, opacity: 0.9 }}>{user.APTI.subtitle}</div>
          </div>
        </div>
      )}

      {/* 다른 사용자 프로필일 때 간단한 소개 카드 */}
      {!isMine && (
        <div style={{
          background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
          borderRadius: 20,
          padding: '24px',
          marginBottom: 32,
          border: '1px solid #e2e8f0',
          boxShadow: '0 4px 16px rgba(0,0,0,0.05)'
        }}>
          <h3 style={{
            fontSize: 18,
            fontWeight: 700,
            color: '#1e293b',
            marginBottom: 16,
            display: 'flex',
            alignItems: 'center',
            gap: 8
          }}>
            📝 소개
          </h3>
          <p style={{
            fontSize: 16,
            lineHeight: 1.6,
            color: '#64748b',
            margin: 0
          }}>
            {introduction || "소개가 없습니다."}
          </p>
        </div>
      )}
      {/* 탭 UI */}
      <div style={styles.tabContainer}>
        <div style={styles.tabScroll}>
          {SPORTS.map(sport => (
            <button 
              key={sport} 
              onClick={() => setSelectedSport(sport)} 
              style={{
                ...styles.tabButton,
                ...(selectedSport === sport ? styles.activeTab : {})
              }}
            >
              {sport}
            </button>
          ))}
          {isAdmin && isMine && (
            <button 
              onClick={() => setSelectedSport('admin')} 
              style={{
                ...styles.tabButton,
                ...(selectedSport === 'admin' ? styles.adminTab : {})
              }}
            >
              👑 관리자
            </button>
          )}
        </div>
      </div>
      {/* 메인 콘텐츠 */}
      {selectedSport === 'admin' ? (
        <AdminPanel 
          user={user} 
          userDocId={userDocId} 
          isAdmin={isAdmin} 
          onRoleChange={fetchUserProfile} 
        />
      ) : (
        <div>
          {/* 전적/통계 카드 */}
          <div style={styles.contentCard}>
            <h3 style={styles.cardTitle}>
              <span>📊</span>
              <span>{selectedSport} 전적</span>
            </h3>
            <div style={styles.statsGrid}>
              <div style={styles.statItem}>
                <div style={styles.statLabel}>승리</div>
                <div style={styles.statValue}>{currentRecord.win}</div>
              </div>
              <div style={styles.statItem}>
                <div style={styles.statLabel}>패배</div>
                <div style={styles.statValue}>{currentRecord.lose}</div>
              </div>
              <div style={styles.statItem}>
                <div style={styles.statLabel}>MMR</div>
                <div style={styles.statValue}>{currentRecord.mmr}</div>
              </div>
              {selectedSport === '볼링' && (
                <div style={styles.statItem}>
                  <div style={styles.statLabel}>평균점수</div>
                  <div style={styles.statValue}>{currentRecord.avgScore}</div>
                </div>
              )}
            </div>
          </div>

          {/* 최근 경기 */}
          <div style={styles.contentCard}>
            <h3 style={styles.cardTitle}>
              <span>⚡</span>
              <span>최근 경기</span>
            </h3>
            {recentMatches.filter(m => m.sport === selectedSport).length === 0 ? (
              <div style={styles.noDataContainer}>
                <div style={styles.noDataIcon}>🎳</div>
                <div style={styles.noDataText}>최근 경기 기록이 없습니다.</div>
              </div>
            ) : (
              <div style={styles.matchList}>
                {recentMatches.filter(m => m.sport === selectedSport).slice(0, 5).map(match => (
                  <div key={match.id} style={styles.matchItem}>
                    <div style={styles.matchHeader}>
                      <div style={styles.matchDate}>
                        {match.date.toLocaleDateString()}
                      </div>
                      <div style={{
                        ...styles.matchResultBadge,
                        ...(match.result === 'win' ? styles.winBadge : styles.loseBadge)
                      }}>
                        {match.result === 'win' ? '승리' : '패배'}
                      </div>
                    </div>
                    <div style={styles.matchScore}>
                      {match.myScore} : {match.oppScore}
                    </div>
                    <div style={styles.matchOpponent}>
                      vs {match.opponent}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* 업적 */}
          <div style={styles.contentCard}>
            <h3 style={styles.cardTitle}>
              <span>🏆</span>
              <span>업적</span>
            </h3>
            {achievements.length === 0 ? (
              <div style={styles.noDataContainer}>
                <div style={styles.noDataIcon}>🏅</div>
                <div style={styles.noDataText}>아직 달성한 업적이 없습니다.</div>
              </div>
            ) : (
              <div style={styles.achievementsGrid}>
                {achievements.map((achievement, index) => (
                  <div key={index} style={styles.achievementItem}>
                    <span style={styles.achievementIcon}>{achievement.icon}</span>
                    <div style={styles.achievementTitle}>{achievement.title}</div>
                    <div style={styles.achievementDesc}>{achievement.description}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* MMR 히스토리 (볼링만) */}
          {selectedSport === '볼링' && mmrHistory.length > 0 && (
            <div style={styles.contentCard}>
              <h3 style={styles.cardTitle}>
                <span>📈</span>
                <span>MMR 변화</span>
              </h3>
              <div style={styles.mmrChart}>
                {mmrHistory.slice(-10).map((record, index) => {
                  const maxMmr = Math.max(...mmrHistory.map(r => r.mmr));
                  const minMmr = Math.min(...mmrHistory.map(r => r.mmr));
                  const height = maxMmr === minMmr ? 80 : ((record.mmr - minMmr) / (maxMmr - minMmr)) * 150 + 30;
                  return (
                    <div 
                      key={index} 
                      style={{
                        ...styles.mmrBar,
                        height: `${height}px`
                      }}
                    >
                      <div style={styles.mmrValue}>{record.mmr}</div>
                      <div style={styles.mmrDate}>
                        {record.date.toLocaleDateString().slice(5)}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
      {/* 친구목록 - 내 마이페이지에서만 노출 */}
      {isMine && (
        <div style={{ marginTop: 32 }}>
          <h3 style={{ fontSize: 20, fontWeight: 800, color: '#6366f1', marginBottom: 16, letterSpacing: 1 }}>👥 친구 목록</h3>
          {friends.length === 0 ? (
            <div style={{ color: '#64748b', fontSize: 16, fontWeight: 600, background: '#f3f4f6', borderRadius: 14, padding: '32px 0', textAlign: 'center', boxShadow: '0 2px 8px #6366f122' }}>
              아직 친구가 없습니다.<br />
              <button onClick={() => navigate('/community/addfriend')} style={{ background: 'linear-gradient(135deg, #6366f1, #a5b4fc)', color: '#fff', border: 'none', borderRadius: 10, padding: '10px 22px', fontWeight: 700, fontSize: 15, marginTop: 18, boxShadow: '0 2px 8px #6366f122', cursor: 'pointer' }}>친구 추가하기</button>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'row', gap: 18, flexWrap: 'wrap', justifyContent: 'flex-start' }}>
              {friends.map(f => (
                <div key={f.id} style={{ minWidth: 210, maxWidth: 260, display: 'flex', alignItems: 'center', gap: 14, background: 'linear-gradient(135deg, #f3f4f6 60%, #e0e7ff 100%)', borderRadius: 16, padding: '14px 18px', boxShadow: '0 2px 8px #6366f122', marginBottom: 8 }}>
                  <div style={{ width: 44, height: 44, borderRadius: 14, overflow: 'hidden', background: '#e0e7ff', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px #6366f122' }}>
                    {f.profileImg ? <img src={f.profileImg + '?t=' + Date.now()} alt="프로필" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : '👤'}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: 16, color: '#3730a3', marginBottom: 2 }}>{f.nickname || f.id}</div>
                  </div>
                  <button onClick={() => navigate(`/community/chat?user=${f.id}`)} style={{ background: 'linear-gradient(135deg, #6366f1, #a5b4fc)', color: '#fff', border: 'none', borderRadius: 8, padding: '7px 13px', fontWeight: 700, fontSize: 14, marginRight: 4, cursor: 'pointer' }}>채팅</button>
                  <button onClick={() => removeFriend(f.id)} disabled={friendLoading} style={{ background: '#e5e7eb', color: '#6366f1', border: 'none', borderRadius: 8, padding: '7px 13px', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>삭제</button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      <div style={{ position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', zIndex: 9999 }}>
        <button onClick={() => window.location.href = '/'} style={{ padding: '16px 40px', borderRadius: 24, background: '#10b981', color: '#fff', fontWeight: 800, fontSize: 20, border: 'none', cursor: 'pointer', boxShadow: '0 4px 16px #10b98133', letterSpacing: 1 }}>
          홈 화면으로 이동
        </button>
      </div>
    </>
  );
}

export default Mypage;